import { Breadcrumb, Button, Layout, Space, Table, Typography } from 'antd';
import { ColumnType } from 'antd/lib/table';
import dayjs from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { IDictionary } from '~utilities/IDictionary';
import IInvestmentSaleTransfer from '~Api/InvestmentSaleTransfer/IInvestmentSaleTransfer';
import {
    investmentSaleTransferListSettingsSetAction,
    investmentSaleTransfersPaginatedListAction,
} from './actions';
import {
    investmentSaleTransferListSettingsSelector,
    investmentSaleTransfersPaginatedCountSelector,
    investmentSaleTransfersPaginatedLoadingSelector,
    investmentSaleTransfersPaginatedSelector,
} from './selectors';
import AddModal from './AddModal';
import './investment-sale-transfers.less';
import IInvestment from '~Api/Investment/IInvestment';
import { currencyFormatter } from '~utilities/formatters';
import { FilterValue, SortOrder, SorterResult, TablePaginationConfig } from 'antd/lib/table/interface';
import ListSortOrderEnum from '~Api/InvestmentSaleTransfer/ListSortOrderEnum';
import ListSortFieldEnum from '~Api/InvestmentSaleTransfer/ListSortFieldEnum';

export interface IInvestmentSaleTransferListSettings {
    currentPage: number;
    pageSize: number;
    sortField: string;
    sortOrder: SortOrder;
}

const tableSortFieldMappings: IDictionary<ListSortFieldEnum> = {
    createdTime: ListSortFieldEnum.CreatedTime,
};

const tableSortOrderMappings: IDictionary<ListSortOrderEnum> = {
    ascend: ListSortOrderEnum.Ascending,
    descend: ListSortOrderEnum.Descending,
};

export default function List(): ReactElement {
    const [ isAddModalOpen, setIsAddModalOpen ] = useState<boolean>(false);

    const investmentSaleTransferListSettings: IInvestmentSaleTransferListSettings = useSelector(investmentSaleTransferListSettingsSelector);
    const investmentSaleTransfers: IDictionary<IInvestmentSaleTransfer> = useSelector(investmentSaleTransfersPaginatedSelector);
    const investmentSaleTransfersPaginatedCount: number = useSelector(investmentSaleTransfersPaginatedCountSelector);
    const investmentSaleTransfersPaginatedLoading: boolean = useSelector(investmentSaleTransfersPaginatedLoadingSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect((): void => {
        if (!investmentSaleTransfers && !investmentSaleTransfersPaginatedLoading) {
            dispatch(investmentSaleTransfersPaginatedListAction(
                investmentSaleTransferListSettings.currentPage,
                investmentSaleTransferListSettings.pageSize,
                ListSortFieldEnum.CreatedTime,
                ListSortOrderEnum.Descending,
            ));
        }
    }, [
        dispatch,
        investmentSaleTransferListSettings,
        investmentSaleTransfers,
        investmentSaleTransfersPaginatedLoading,
    ]);

    const onChangeTable: (pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: any) => void = useCallback((pagination: TablePaginationConfig, filters: Record<string, FilterValue | null>, sorter: any): void => {
        const actualSorter: SorterResult<IInvestmentSaleTransfer> = sorter as SorterResult<IInvestmentSaleTransfer>;
        const sortField: ListSortFieldEnum = tableSortFieldMappings[actualSorter.columnKey] || ListSortFieldEnum.CreatedTime;

        dispatch(investmentSaleTransfersPaginatedListAction(
            pagination.current,
            pagination.pageSize,
            sortField,
            tableSortOrderMappings[sorter.order] || ListSortOrderEnum.Descending,
        ));

        dispatch(investmentSaleTransferListSettingsSetAction({
            currentPage: pagination.current,
            pageSize: pagination.pageSize,
            sortField,
            sortOrder: actualSorter.order,
        }));
    }, [
        dispatch,
    ]);

    const onClickAdd: () => void = useCallback((): void => {
        setIsAddModalOpen(true);
    }, []);

    const onCloseAddModal: () => void = useCallback((): void => {
        setIsAddModalOpen(false);
    }, []);

    const columns: ColumnType<IInvestmentSaleTransfer>[] = [
        {
            key: 'createdTime',
            render: (investmentSaleTransfer: IInvestmentSaleTransfer): ReactElement => <Link to={`/investment-sale-transfers/${investmentSaleTransfer.uuid}/transactions`}>{dayjs(investmentSaleTransfer.createdTime).format('DD/MM/YYYY HH:mm')}</Link>,
            sortOrder: investmentSaleTransferListSettings.sortField === ListSortFieldEnum.CreatedTime ? investmentSaleTransferListSettings.sortOrder : null,
            sorter: true,
            title: 'Created',
            width: 180,
        },
        {
            dataIndex: 'investment',
            key: 'investment',
            render: (investment: IInvestment): string => investment.code,
            title: 'Investment',
            width: 150,
        },
        {
            dataIndex: 'investment',
            key: 'loan',
            render: (investment: IInvestment): string => investment.loanCode,
            title: 'Loan',
            width: 150,
        },
        {
            key: 'transactionCount',
            render: (investmentSaleTransfer: IInvestmentSaleTransfer): number => investmentSaleTransfer.transactionCount,
            title: 'Transaction Count',
            width: 160,
        },
        {
            key: 'transactionTotal',
            render: (investmentSaleTransfer: IInvestmentSaleTransfer): string => currencyFormatter.format(investmentSaleTransfer.transactionAmountTotal),
            title: 'Transaction Total',
            width: 160,
        },
    ];

    return (
        <Layout>
            <Breadcrumb className='breadcrumb'>
                <Breadcrumb.Item>Home</Breadcrumb.Item>
                <Breadcrumb.Item>Investment Sale Transfers</Breadcrumb.Item>
            </Breadcrumb>
            <Layout className='content-wrapper'>
                <Layout.Content className='content'>
                    <Space className='actions'>
                        <Button onClick={onClickAdd}>Add Investment Sale Transfer</Button>
                    </Space>
                    <Typography.Title level={2}>Investment Sale Transfers</Typography.Title>
                    <Table
                        columns={columns}
                        dataSource={_.values(investmentSaleTransfers)}
                        loading={investmentSaleTransfersPaginatedLoading}
                        onChange={onChangeTable}
                        pagination={{ current: investmentSaleTransferListSettings.currentPage, pageSize: investmentSaleTransferListSettings.pageSize, showSizeChanger: true, total: investmentSaleTransfersPaginatedCount }}
                        rowKey='uuid'
                        scroll={{ x: 1 }}
                        size='middle'
                    />
                </Layout.Content>
            </Layout>
            <AddModal
                isOpen={isAddModalOpen}
                onCancel={onCloseAddModal}
            />
        </Layout>
    );
}
